import { useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { UserRole } from '@/types/api';

const ROLE_RANKS: Record<UserRole, number> = {
  [UserRole.Owner]: 4,
  [UserRole.Admin]: 3,
  [UserRole.Manager]: 2,
  [UserRole.Viewer]: 1,
};

const ALL_ROLE_OPTIONS = [
  { value: UserRole.Owner, label: 'Sys Admin', description: 'System-wide access across all organizations' },
  { value: UserRole.Admin, label: 'Admin', description: 'Full org access including user management' },
  { value: UserRole.Manager, label: 'Manager', description: 'Manage properties, devices, and alerts' },
  { value: UserRole.Viewer, label: 'Viewer', description: 'Read-only access to org data' },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string, role: UserRole) => Promise<void>;
  callerRole?: UserRole;
}

export function AddUserModal({ isOpen, onClose, onSubmit, callerRole }: Props) {
  const callerRank = callerRole ? ROLE_RANKS[callerRole] : 1;
  const roleOptions = ALL_ROLE_OPTIONS.filter(o => ROLE_RANKS[o.value] <= callerRank);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.Viewer);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  function handleClose() {
    setEmail('');
    setRole(UserRole.Viewer);
    setError('');
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setError('');
    setSubmitting(true);
    try {
      await onSubmit(email.trim(), role);
      setEmail('');
      setRole(UserRole.Viewer);
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined;
      setError(message || 'Failed to add user');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add User" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Email address"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="user@example.com"
          required
        />
        <Select
          label="Role"
          options={roleOptions}
          value={role}
          onChange={value => setRole(value as UserRole)}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          An invite email will be sent. The user must accept before they're added to the organization.
        </p>
        {error && <p className="text-sm text-error-600">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
          <Button type="submit" isLoading={submitting} disabled={!email.trim()}>
            Add User
          </Button>
        </div>
      </form>
    </Modal>
  );
}
